import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Building2, LayoutDashboard, PlusCircle, FileText, DollarSign, FileSignature, User, LogOut, Menu, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/partner/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/partner/submit-lead", label: "Submit Lead", icon: PlusCircle },
  { to: "/partner/leads", label: "My Leads", icon: FileText },
  { to: "/partner/payments", label: "Payments", icon: DollarSign },
  { to: "/partner/agreement", label: "Agreement", icon: FileSignature },
  { to: "/partner/profile", label: "Profile", icon: User },
];

export const PartnerLayout = () => {
  const [partnerName, setPartnerName] = useState("");
  const [partnerStatus, setPartnerStatus] = useState<string | null>(null);
  const [agreementSigned, setAgreementSigned] = useState<boolean | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data } = await supabase
        .from("referral_partners")
        .select("agent_name, agreement_signed_at, status")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (data) {
        setPartnerName(data.agent_name);
        setAgreementSigned(!!data.agreement_signed_at);
        setPartnerStatus(data.status);
      }
    };
    load();

    // Listen for agreement signing within the session
    const channel = supabase
      .channel('partner-agreement')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'referral_partners' }, (payload) => {
        if (payload.new.agreement_signed_at) {
          setAgreementSigned(true);
        }
        if (payload.new.status) {
          setPartnerStatus(payload.new.status as string);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Redirect to agreement page if not signed (allow agreement & profile pages)
  useEffect(() => {
    if (agreementSigned === false && location.pathname !== "/partner/agreement" && location.pathname !== "/partner/profile") {
      navigate("/partner/agreement", { replace: true });
    }
  }, [agreementSigned, location.pathname, navigate]);

  // For pending partners who signed agreement, block lead submission
  const isPending = partnerStatus === "pending";

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/partner/login");
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Top Bar */}
      <header className="bg-primary text-primary-foreground shadow-md sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Building2 className="h-6 w-6" />
            <span className="font-bold text-lg hidden sm:inline">Partner Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm hidden sm:inline">{partnerName}</span>
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-primary-foreground hover:bg-primary-light">
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Pending review banner */}
      {isPending && agreementSigned && (
        <div className="bg-secondary/20 border-b border-secondary/30 px-4 py-3">
          <div className="flex items-center gap-2 justify-center text-sm text-secondary-foreground">
            <AlertTriangle className="h-4 w-4 text-secondary" />
            <span>Your application is under review. You'll be notified by email once approved.</span>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? "block" : "hidden"} md:block w-64 bg-card border-r border-border min-h-[calc(100vh-4rem)] p-4 fixed md:sticky top-16 z-40`}>
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              const isAgreementPage = item.to === "/partner/agreement";
              const isProfilePage = item.to === "/partner/profile";
              const isSubmitLead = item.to === "/partner/submit-lead";
              const isDisabledByAgreement = agreementSigned === false && !isAgreementPage && !isProfilePage;
              const isDisabledByPending = isPending && isSubmitLead;
              const isDisabled = isDisabledByAgreement || isDisabledByPending;
              
              return (
                <Link
                  key={item.to}
                  to={isDisabled ? "#" : item.to}
                  onClick={(e) => {
                    if (isDisabledByAgreement) {
                      e.preventDefault();
                      navigate("/partner/agreement");
                    } else if (isDisabledByPending) {
                      e.preventDefault();
                    }
                    setMobileOpen(false);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isDisabled
                        ? "text-muted-foreground/50 cursor-not-allowed"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {isAgreementPage && agreementSigned === false && (
                    <span className="ml-auto bg-destructive text-destructive-foreground text-[10px] px-1.5 py-0.5 rounded-full font-bold">Required</span>
                  )}
                  {isDisabledByPending && (
                    <span className="ml-auto bg-secondary/20 text-secondary text-[10px] px-1.5 py-0.5 rounded-full font-bold">Pending</span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="mt-8 p-3 bg-muted rounded-lg border border-border">
            <p className="text-xs text-muted-foreground">
              <strong>Fee Schedule:</strong><br />
              Local Move: $50 flat fee<br />
              Long Distance: $100 flat fee<br />
              <span className="text-[10px]">Paid to Managing Broker per IL law</span>
            </p>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 md:ml-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
